import { apiv2Get } from '@/lib/apiv2';
import { eventPost } from '@/lib/events';

export interface CarePlan {
  care_plan_id?: string | number;
  withings_user_id: string | number;
  is_current?: boolean;
  version?: number;
  created?: string; // apiv2 creation ts (ISO) — the field apiv2 actually returns
  created_at?: string; // contract-spec alias; absent on apiv2 reads
  modified?: string; // apiv2 last-update ts (ISO) — the field apiv2 actually returns
  updated_at?: string; // contract-spec alias; absent on apiv2 reads
  [key: string]: unknown;
}

// apiv2 has no `order` filter on care-plans — fetch and order newest-first client-side.
export async function getCarePlans(): Promise<CarePlan[]> {
  const rows = (await apiv2Get('care-plans', { limit: 200 })) as CarePlan[] | null;
  return (rows || [])
    .slice()
    .sort((a, b) => String(b.created ?? b.created_at ?? '').localeCompare(String(a.created ?? a.created_at ?? '')));
}

export async function getCarePlanByUser(withingsUserId: string | number): Promise<CarePlan | null> {
  // No `limit:1`+order on apiv2 — fetch the patient's plans and pick the current one
  // (`is_current`), falling back to the newest by `created_at`.
  const rows = (await apiv2Get('care-plans', {
    withings_user_id: String(withingsUserId),
  })) as CarePlan[] | null;
  if (!rows || rows.length === 0) return null;
  return (
    rows.find((r) => r.is_current)
    ?? rows
      .slice()
      .sort((a, b) => String(b.created ?? b.created_at ?? '').localeCompare(String(a.created ?? a.created_at ?? '')))[0]
    ?? null
  );
}

// Tier 4 care_plan_updated → WF15 cascade runs server-side (history snapshot, content reconcile, etc.).
export async function updateCarePlan(
  withingsUserId: string | number,
  updates: Record<string, unknown>,
): Promise<unknown> {
  return eventPost('care_plan_updated', {
    id_dev_user: String(withingsUserId),
    trigger_source: 'care_team_ui',
    changes: updates,
  });
}
